import { CloudProviders } from '@ariksa/cloud-account';
import { SnowflakeTagInfo, SnowflakeTagRef } from '@ariksa/data-scanning/api';

export const dataSourceTypeOptions = [
  { label: 'Snowflake', value: CloudProviders.Snowflake },
];

// Skip-tag helpers: customers pick their own Snowflake tags; columns carrying
// any of them (directly or inherited) are left untagged by the label sync.
export interface SkipTagOption {
  label: string;
  value: string;
  tag: SnowflakeTagRef;
  isMissing?: boolean;
}

export const getTagKey = (tag: SnowflakeTagRef) =>
  `${tag.database}.${tag.schema_name}.${tag.name}`;

const toOption = (tag: SnowflakeTagRef, isMissing = false): SkipTagOption => ({
  label: isMissing
    ? `${getTagKey(tag)} (not found in Snowflake)`
    : getTagKey(tag),
  value: getTagKey(tag),
  tag: { database: tag.database, schema_name: tag.schema_name, name: tag.name },
  isMissing,
});

/** Options = tags found in Snowflake plus saved tags that no longer exist. */
export const buildSkipTagOptions = (
  available: SnowflakeTagInfo[],
  saved: SnowflakeTagRef[],
): { options: SkipTagOption[]; selected: SkipTagOption[] } => {
  const options = available.map(tag => toOption(tag));
  const byKey = new Map(options.map(option => [option.value, option]));
  const selected = saved.map(tag => {
    const existing = byKey.get(getTagKey(tag));
    if (existing) return existing;
    const missing = toOption(tag, true);
    options.push(missing);
    byKey.set(missing.value, missing);
    return missing;
  });
  return { options, selected };
};

export const toSkipTags = (selected: SkipTagOption[] | null | undefined) =>
  (selected ?? []).map(option => option.tag);

export const isSameSkipTags = (a: SnowflakeTagRef[], b: SnowflakeTagRef[]) => {
  const keys = new Set(a.map(getTagKey));
  return a.length === b.length && b.every(tag => keys.has(getTagKey(tag)));
};
